<template>
  <main v-if="project">
    <h2>Project: {{ project.name }}</h2>
    <div v-if="project.done_date" class="done">
      <p>This project is marked as done and no further changes can be made.</p>
    </div>
    <div class="assessment" v-if="project.assessment">
      <h3>Assessment</h3>
      <div class="markdown-content" v-html="markdown2(project.assessment)"></div>
    </div>
    <div class="skills">
      <!-- Editovatelný režim (před request_date) -->
      <div v-if="!project.request_date" class="skills-edit">
        <h3>Skills:</h3>
        <div class="skills-list">
          <div v-for="skill in allSkills" :key="skill.id" class="skill-item">
            <RouterLink
              :to="{ name: 'skill-detail', params: { id: skill.id } }"
              target="_blank"
            >🔎</RouterLink><label
              :for="'skill-' + skill.id"
              :title="skill.description"
              :style="{
                backgroundColor: skill.color,
                color: getTextColor(skill.color)
              }"
              class="skill-checkbox"
            >
              <input
                type="checkbox"
                :id="'skill-' + skill.id"
                :value="skill.id"
                v-model="selectedSkillIds"
              />
              {{ skill.name }} ({{ skill.weight }})
            </label>
          </div>
        </div>
        <button @click="saveSkills" class="save-skills-btn">Save Skills</button>
      </div>

      <!-- Čtecí režim (po request_date) -->
      <dl v-else>
        <dt>Skills:</dt>
        <dd v-for="skill in project.skills" :key="skill.id" :style="{ backgroundColor: skill.color }">
          <RouterLink :to="{ name: 'skill-detail', params: { id: skill.id } }"
            :style="{ color: getTextColor(skill.color) }"
            :title="skill.description"
            class="skill-link">
            {{ skill.name }}({{ skill.weight }})
          </RouterLink>
        </dd>
      </dl>
    </div>
    <div class="description">
      <div class="buttons" v-if="!project.request_date">
        <button @click="sourcedDescription">{{ isSourced ? 'Preview' : 'Edit' }}</button>
        <button @click="saveDescription">Save</button>
        <button @click="toggleExpand">{{ isExpanded ? 'Collapse' : 'Expand' }}</button>
        <label v-if="isSourced" class="dyslexic-toggle">
          <input type="checkbox" v-model="useDyslexicFont" />OpenDyslexic
        </label>
      </div>
      <div class="buttons" v-else>
        <button @click="toggleExpand">{{ isExpanded ? 'Collapse' : 'Expand' }}</button>
      </div>
      <div v-if="!isSourced || project.request_date" v-html="markdown(project.description)" class="markdown-content" :style="{
        maxHeight: isExpanded ? 'none' : '20rem',
        overflow: isExpanded ? 'hidden' : 'auto',
      }"></div>
      <div v-else>
        <textarea v-model="project.description"
          :style="{ fontFamily: useDyslexicFont ? '\'OpenDyslexicMono\', monospace' : 'monospace' }"
        ></textarea>
      </div>
      <div class="buttons" v-if="!project.request_date">
        <button @click="sourcedDescription">{{ isSourced ? 'Preview' : 'Edit' }}</button>
        <button @click="saveDescription">Save</button>
        <button @click="toggleExpand">{{ isExpanded ? 'Collapse' : 'Expand' }}</button>
        <label v-if="isSourced" class="dyslexic-toggle">
          <input type="checkbox" v-model="useDyslexicFont" />
          OpenDyslexic
        </label>
      </div>
      <div class="buttons" v-else>
        <button @click="toggleExpand">{{ isExpanded ? 'Collapse' : 'Expand' }}</button>
      </div>
    </div>

    <div class="consign">
      <VideoUpload :project-id="project.id" :video-file="project.video_file" :video-required="project.video_required"
        @video-updated="handleVideoUpdate" />
    </div>

    <div class="urls-notes">
      <div class="urls">
        <p>
          <label for="repo-URL">Git Repo URL:</label><br />
          <input id="repo-URL" v-model="project.repo_URL" :class="{ error: repoURLError }" />
          <span v-if="repoURLError" class="error-message">This must be a valid URL</span>
          <a v-else :href="project.repo_URL" target="_blank" rel="noopener noreferrer">{{ project.repo_URL }}</a>
        </p>
        <p>
          <label for="another-URLs">Additional URLs:</label><br />
          <textarea id="another-URLs" v-model="project.another_URLs" rows="5" cols="40"
            :class="{ error: anotherURLsError }"></textarea>
          <span v-if="anotherURLsError" class="error-message">Every line must be a valid URL</span>
        <ul v-else>
          <li v-for="url in project.another_URLs?.split('\n') ?? []" :key="url">
            <a :href="url" target="_blank" rel="noopener noreferrer">{{ url }}</a>
          </li>
        </ul>
        </p>
      </div>
      <div class="notes">
        <p>
          <label for="notes">Notes:</label><br />
          <textarea id="notes" v-model="project.notes" rows="7"></textarea>
          <button @click="cancelChanges" :disabled="!isFormChanged">Cancel changes</button>
          <button @click="saveURL_and_Notes" :disabled="!isFormChanged || repoURLError || anotherURLsError">
            Save
          </button>
        </p>
      </div>
    </div>

    <div class="actions">
      <p>
        <span>Request:</span><br />
        <span v-if="project.request_date">{{ project.request_date }}</span>
        <button v-else @click="createRequest">Submit for confirmation</button>
      </p>
      <p>
        <span>Confirm:</span><br />
        <span v-if="project.confirm_date">✅ {{ project.confirm_date }} ✅</span>
        <span v-else>❌ This project is not confirmed ❌</span>
      </p>
      <p>
        <span>Done:</span><br />
        <span v-if="project.done_date">{{ project.done_date }}</span>
        <button v-if="project.request_date && project.confirm_date && !project.done_date
          && !isFormChanged
          && project.repo_URL
          && (project.video_file || !project.video_required)" @click="createDone">
          Mark this project as done
        </button>
        <button v-else disabled>Mark this project as done</button>
        <small v-if="doneHints.length" class="done-hints">
          {{ doneHints.join(' · ') }}
        </small>
      </p>
    </div>
    <div class="danger" v-if="!project.request_date">
      <p>
        <label for="rename">Rename this project:</label>
        <input type="text" name="rename" v-model="project.name">
        <button @click="renameProject">Rename</button>
      </p>
      <p>
        Delete this project:
        <button @click="deleteProject">Delete this project</button>
      </p>
    </div>
  </main>
  <main v-else>
    <loading :status="status" />
  </main>
</template>

<style lang="scss" scoped>
@use 'sass:color';

h2 {
  margin-bottom: 2rem;
}

.done {
  text-align: center;

  p {
    display: inline-block;
    padding: 14px;
    border: 3px solid $btnColor;
    border-radius: 0.4rem;
    font-weight: bold;
  }
}


.assessment {
  margin: 1rem auto;
  background-color: rgba($primari, 0.15);
  padding: 11px;
  border-radius: 8px;

  h3 {
    font-weight: bold;
  }

  div {
    margin-left: 3ex;
    padding: 1rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #f9f9f9;
  }
}

.actions {
  display: flex;
  border-bottom: 2px solid $primari;
  padding-bottom: 1ex;

  p {
    flex: 1;
  }

  button {
    padding: 0.4rem;
    border-radius: 0.4rem;
    font-weight: bold;
    color: white;
    background-color: $primari;
    font-size: 1.2rem;

    &:hover {
      background-color: color.scale($primari, $lightness: -10%);
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    }

    &:active {
      background-color: color.scale($primari, $lightness: -15%);
      box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.2);
      transform: translateY(1px);
    }

    &:focus {
      outline: 1px solid black;
      outline-offset: 1px;
    }
  }

  .done-hints {
    display: block;
    color: #a00;
    font-size: 0.9rem;
    margin-top: 0.3rem;
  }
}

.skills {
  border-bottom: 2px solid $primari;

  dd {
    display: inline-block;
    border-radius: 1rem;
    margin: 0.5rem;

    a {
      display: inline-block;
      padding: 0.5rem 1rem;
    }

  }

  .skills-edit {
    padding: 1rem;
    background-color: #f9f9f9;
    border-radius: 8px;
    margin-bottom: 1rem;

    h3 {
      margin-bottom: 1rem;
    }

    .skills-list {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-bottom: 1rem;
    }

    .skill-item {
      padding-left: 1.2rem;
      // display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .skill-checkbox {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      padding: 0.5rem;
      border-radius: 7px;
      cursor: pointer;

      input[type="checkbox"] {
        cursor: pointer;
      }

    }

    .save-skills-btn {
      padding: 0.5rem 1rem;
      background-color: $btnColor;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-weight: bold;

      &:hover {
        background-color: color.scale($btnColor, $lightness: -10%);
      }

      &:active {
        background-color: color.scale($btnColor, $lightness: -15%);
        box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.2);
        transform: translateY(1px);
      }
    }
  }
}

.description {
  border-bottom: 2px solid $primari;
  position: relative;

  .buttons {
    position: relative;
    left: calc(100% - 26rem);

    display: inline-block;
    margin: 12px;
    padding: 11px;
    background-color: #eee;
    border-radius: 0.4rem;

    button {
      display: inline-block;
      padding: 0.4rem;
      border-radius: 0.4rem;
      font-weight: bold;
      background-color: $btnColor;
      color: white;

      &:hover {
        background-color: color.scale($btnColor, $lightness: -10%);
      }

      &:active {
        background-color: color.scale($btnColor, $lightness: -15%);
        box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.2);
        transform: translateY(1px);
      }

    }

    .dyslexic-toggle {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      cursor: pointer;
      font-size: 0.9rem;
      user-select: none;
    }
  }

  textarea {
    display: block;
    margin: auto;
    padding: 12px;
    width: 88%;
    height: 56rem;
    max-height: 77vh;

    &:focus {
      // outline-offset: 0px;
      // outline: 2px solid $btnColor;
      outline-width: 0;
      box-shadow: 0 0 5px 5px $btnColor;

    }
  }

  >div.markdown-content {
    overflow: auto;
    border-top: 2px solid #ddd;
    border-bottom: 3px solid #ddd;
  }
}

.urls-notes {
  background-color: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #ddd;

  button {
    margin-left: 3rem;
    padding: 0.5rem 1rem;
    background-color: $btnColor;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: color.scale($btnColor, $lightness: -10%);
    }

    &:active {
      background-color: color.scale($btnColor, $lightness: -15%);
      box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.2);
      transform: translateY(1px);
    }
  }
}

.urls {
  margin: 1rem auto;
  padding: 8px;


  display: flex;

  p {
    flex: 1;
  }

  input,
  textarea {
    width: 90%;
  }
}

.notes {
  padding: 8px 20px;

  textarea {
    margin: auto;
    width: 44ex;
    max-width: 88%;
  }
}

input.error,
textarea.error {
  border: 2px solid #ff3333;
  background-color: #fff8f8;
}

.error-message {
  color: #ff3333;
  font-size: 0.8rem;
  display: block;
  margin-top: 0.2rem;
}

button:disabled {
  background-color: #cccccc !important;
  color: #666666 !important;
  cursor: not-allowed;
  box-shadow: none !important;
  transform: none !important;
}

.notes button {
  transition: all 0.2s ease;
}

.danger {
  padding: 3rem;
  background-color: rgba(255, 0, 0, 0.3);
  color: $btnColor;

  p {
    margin-bottom: 3rem;
  }

  button {
    padding: 0.5rem 1rem;
    color: white;
    background-color: #a00;
    font-weight: bold;
    border-radius: 0.3rem;
  }
}
</style>

<script setup>
import { useRoute } from 'vue-router'
import { ref, onMounted, watch, computed } from 'vue'
import { api } from '@/utils/axios'
import { getTextColor, markdown, markdown2 } from '@/utils/helpers'
import { useMessagesStore } from '@/stores/messages'
import VideoUpload from '@/components/VideoUpload.vue'
import confirmDialog from '@/utils/confirmDialog'
import loading from '@/components/loading.vue'
import router from '../router'


const messages = useMessagesStore()
const route = useRoute()
const projectId = route.params.id
const project = ref(null)
const status = ref('loading')
const isExpanded = ref(false)
const isSourced = ref(true)
const useDyslexicFont = ref(localStorage.getItem('dyslexicFont') !== 'false')
let repoURLOriginal, anotherURLsOriginal, notesOriginal
const repoURLError = ref(false)
const anotherURLsError = ref(false)
const allSkills = ref([])
const selectedSkillIds = ref([])
const isFormChanged = computed(() => {
  return (
    (project.value.repo_URL || '') != repoURLOriginal ||
    (project.value.another_URLs || '') != anotherURLsOriginal ||
    (project.value.notes || '') != notesOriginal
  )
})

const doneHints = computed(() => {
  if (!project.value || project.value.done_date) return []
  const hints = []
  if (!project.value.request_date) hints.push('Project must have a request')
  if (!project.value.confirm_date) hints.push('Project must be confirmed')
  if (isFormChanged.value) hints.push('All changes must be saved')
  if (!project.value.repo_URL) hints.push('Git Repo URL must be filled')
  if (project.value.video_required && !project.value.video_file) hints.push('Video must be uploaded')
  return hints
})

const urlRegex = /^(https?:\/\/)(www\.)?([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\:[0-9]{1,5})?(\/[^\s]*)?$/;
const gitRegex = /^(?:(?:https?):\/\/(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?::[0-9]{1,5})?(?:\/[A-Za-z0-9._~:/?#[\]@!$&'()*+,;=-]+)+|git@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)*[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?:[A-Za-z0-9._~\/?#[\]@!$&'()*+,;=-]+)(?:\.git)?$/;

onMounted(async () => {
  try {
    const response = await api.get(`/project/${projectId}`)
    project.value = response.data
    initOriginalValues()
    validateForm()

    // Načíst všechny dostupné skills
    const skillsResponse = await api.get('/skills')
    allSkills.value = skillsResponse.data
    selectedSkillIds.value = project.value.skills.map(s => s.id)
  } catch (error) {
    status.value = error.response?.data?.msg || 'Error fetching projects data'
    messages.error('❌ Error: ' + (error.response?.data?.msg || error.message))
  }
})

// watch(
//   () => project.value,
//   () => {
//     console.log(project.value)
//   },
// )

function handleVideoUpdate(newVideoUrl) {
  if (project.value) {
    project.value.video_file = newVideoUrl
  }
}

function sourcedDescription() {
  isSourced.value = !isSourced.value
  if (isSourced.value) {
    isExpanded.value = true
  }
}

async function saveDescription() {

  const formData = new FormData()
  formData.append('description', project.value.description)
  try {
    await api.post(`/save-description/${projectId}`, formData)
    messages.info('👍 Description saved.')
  } catch (error) {
    messages.error('❌ Error: ' + (error.response?.data?.msg || error.message))
  }

}

function toggleExpand() {
  isExpanded.value = !isExpanded.value
}

async function createRequest() {
  const confirmed = await confirmDialog.confirm({
    title: 'Submit for confirmation?',
    message: 'Really submit this project for confirmation?',
    confirmText: 'Yes',
    cancelText: 'No',
  })
  if (!confirmed) return

  try {
    const response = await api.post(`/project/set/${projectId}`, {
      request_date: true,
    })
    messages.info('👍 Request created.')

    const projectResponse = await api.get(`/project/${projectId}`)
    project.value = projectResponse.data
  } catch (error) {
    messages.error('❌ Error: ' + (error.response?.data?.msg || error.message))
  }
}

async function createDone() {
  const confirmed = await confirmDialog.confirm({
    title: 'Mark this project as done?',
    message: 'Really mark this project as done? No further changes will be possible.',
    confirmText: 'Yes',
    cancelText: 'No',
  })
  if (!confirmed) return

  try {
    const response = await api.post(`/project/set/${projectId}`, {
      done_date: true,
    })
    messages.info(response.data.msg)

    const projectResponse = await api.get(`/project/${projectId}`)
    project.value = projectResponse.data
  } catch (error) {
    messages.error('❌ Error: ' + (error.response?.data?.msg || error.message))
  }
}

function initOriginalValues() {
  if (project.value) {
    repoURLOriginal = project.value.repo_URL || ''
    anotherURLsOriginal = project.value.another_URLs || ''
    notesOriginal = project.value.notes || ''
  }
}

function validateRepoURL(url) {
  if (!url) return true // Prázdná hodnota je povolena
  return gitRegex.test(url)
}

function validateAnotherURLs(urls) {
  if (!urls) return true // Prázdná hodnota je povolena
  const lines = urls.split('\n').filter((line) => line.trim() !== '')
  return lines.every((line) => urlRegex.test(line.trim()))
}

function validateForm() {
  repoURLError.value = !validateRepoURL(project.value.repo_URL)
  anotherURLsError.value = !validateAnotherURLs(project.value.another_URLs)
  return !repoURLError.value && !anotherURLsError.value
}

async function saveURL_and_Notes() {
  if (!validateForm()) {
    messages.error('❌ Error: Please check the entered URLs')
    return
  }

  try {
    const response = await api.post(`/project/set/${projectId}`, {
      repo_URL: project.value.repo_URL,
      another_URLs: project.value.another_URLs,
      notes: project.value.notes,
    })

    messages.info('👍 Changes saved.')
    try {
      const response = await api.get(`/project/${projectId}`)
      project.value = response.data
      initOriginalValues()
      validateForm()
    } catch (error) {
      status.value = error.response?.data?.msg || 'Error fetching projects data'
    }
  } catch (error) {
    messages.error('❌ Error: ' + (error.response?.data?.msg || error.message))
  }
}

async function cancelChanges() {
  try {
    const response = await api.get(`/project/${projectId}`)
    project.value = response.data
    initOriginalValues()
    validateForm()
  } catch (error) {
    status.value = error.response?.data?.msg || 'Error fetching projects data'
  }
}

watch(useDyslexicFont, (val) => {
  localStorage.setItem('dyslexicFont', val)
})

watch(
  () => project.value?.repo_URL,
  (newVal) => {
    if (project.value) {
      repoURLError.value = !validateRepoURL(newVal)
    }
  },
)

watch(
  () => project.value?.another_URLs,
  (newVal) => {
    if (project.value) {
      anotherURLsError.value = !validateAnotherURLs(newVal)
    }
  },
)

async function saveSkills() {
  try {
    const response = await api.post(`/project/${projectId}/skills`, {
      skills: selectedSkillIds.value
    })
    messages.success('👍 ' + response.data.msg)

    // Refresh projektu
    const projectResponse = await api.get(`/project/${projectId}`)
    project.value = projectResponse.data
    selectedSkillIds.value = project.value.skills.map(s => s.id)
  } catch (error) {
    messages.error('❌ Error: ' + (error.response?.data?.msg || error.message))
  }
}

async function renameProject() {
  try {
    const response = await api.post(`/project/rename/${projectId}`, {
      name: project.value.name,
    })
    messages.success('👍 ' + response.data.msg)
  } catch (error) {
    messages.error('❌ Error: ' + (error.response?.data?.msg || error.message))
  }
}

async function deleteProject() {
  const confirmed = await confirmDialog.confirm({
    title: 'Delete project?',
    message: 'Really delete this project? This action cannot be undone.',
    confirmText: 'Yes',
    cancelText: 'No',
  })
  if (!confirmed) return

  try {
    const response = await api.post(`/project/delete/${projectId}`, { delete: true })
    messages.success('👍 ' + response.data.msg)
    router.push('/projects')
  } catch (error) {
    messages.error('❌ Error: ' + (error.response?.data?.msg || error.message))
  }

}

</script>
